import * as moment from "moment";
import styled from "styled-components";

export const colors = {
  red: "#AB0028",
  orange: "#bf6900",
  yellow: "#bfbf22",
  green: "#46a800",
  blue: "#21b9db",
};

export const categories = {
  id: "id",
  user: "name",
  userId: "userId",
  agreements: "New Agreements",
  labor: "Labor Profit",
  hardwareSoftware: "Hardware / Software Profit",
  quotaAverage: "Quota Average",
};

const combinable = [
  "New Agreements",
  "Labor Profit",
  "Hardware / Software Profit",
  "Quota Average",
];

export const barColor = quotaPercent => {
  switch (true) {
    case quotaPercent === null:
      return colors["blue"];
    case quotaPercent < 50:
      return colors["red"];
    case quotaPercent < 75:
      return colors["orange"];
    case quotaPercent <= 99:
      return colors["yellow"];
    case quotaPercent > 99:
      return colors["green"];
    default:
      return colors["blue"];
  }
};

// Filters the data to remove attributes
export const getDetailedBarData = (myObj, filteredList) => {
  const detailedData = Object.keys(myObj)
    .filter(key => !filteredList.includes(key))
    .map(i => {
      let newObj = {};
      newObj[i] = myObj[i];
      newObj["label"] = i;
      return newObj;
    });
  return detailedData;
};

// Format the data
export const formatBarData = (data, user = null) => {
  if (data && data.length > 0) {
    const formattedData = data
      .filter(obj => obj.resultCache) // remove empty values
      .map(obj => {
        //create the base object
        const newObj = user
          ? { id: obj.id, userId: user.id, name: user.name }
          : { id: obj.id, userId: obj.user.id, name: obj.user.name };
        for (let key in obj["resultCache"]) {
          // look up new key value
          const newKey = categories[key];
          if (newKey) {
            // check if percent quota in result cache, round it if it is
            if (obj["resultCache"][key]["percentQuota"] != null) {
              newObj[newKey] = Math.round(
                obj["resultCache"][key]["percentQuota"]
              );
            } else {
              // If quota average, round the value
              if (newKey === "Quota Average") {
                newObj[newKey] = Math.round(obj["resultCache"][key]);
              } else {
                newObj[newKey] = obj["resultCache"][key];
              }
            }
          }
        }
        return newObj;
      });
    return formattedData;
  }
  return data;
};

// Find the most recent commission from array
// given an array of commisions returns the last commission's month date.
// if all commisions are filterd out, to precent error we return current month date.
export const findLatestCommission = data => {
  const filtered_c = data.filter(commission => commission.resultCache !== null);
  let last_c_moment= [];
  if(filtered_c.length == 0){
    last_c_moment.push(moment().toDate())
  }
  else{
    last_c_moment = filtered_c.map(commission => moment(commission.month))
  }
  return new Date(
    Math.max.apply(
      null,
      last_c_moment
    )
  );

};

// Find all years for commissions
// Receives an array of commissions
// Returns a list of unique years
export const findCommissionYears = data => {
  if (data && Array.isArray(data) && data.length > 0) {
    // Return a list of unique years
    const years = data
      .map(commission => moment(commission["month"]).format("YYYY"))
      .sort((a, b) => Number(b) - Number(a));
    return [...new Set(years)];
  }
  return data;
};

// Creates the options and values for react-select
// Receives an array of values
// Returns an array of objects with label and value properties
export const createOptionsList = data => {
  if (data && Array.isArray(data) && data.length > 0) {
    return data.map(year => ({ label: year, value: year }));
  }
  return data; // No data, return.
};

// Method to combine the results from same users and get the overall average.
export const combineCommissions = (data, matchKey) => {
  // Declare new array
  const mergedCommissions = [];
  // Loop through the data if there was data and its an array
  if (data && Array.isArray(data) && data.length > 0) {
    data.forEach(commission => {
      // Check if exists in the output array
      const foundInArray = mergedCommissions.some(
        mergedCommissionsObj =>
          mergedCommissionsObj[matchKey] === commission[matchKey]
      );
      // Ignore if its already in the output array
      if (!foundInArray) {
        // Get all entries for user and put into array
        // if greater then 1 result, then combine those results and put into new array
        // If exactly 1 result then append to new array
        const userCommissions = data.filter(dataCommission => {
          if (dataCommission[matchKey] === commission[matchKey]) {
            return dataCommission;
          }
        });
        if (userCommissions.length > 1) {
          // combine results then append
          const combinedCommission = calculateAverages(userCommissions);
          mergedCommissions.push(combinedCommission);
        } else {
          // just append
          mergedCommissions.push(commission);
        }
      }
    });
    return mergedCommissions; // Return the new array
  }
  return data; // Return if data wasn't an array or no values
};

// Combines all commissions (doesn't take user into account)
// Used to get team average
export const combineCommissionsAll = (data, setTeam = true) => {
  if (data && Array.isArray(data) && data.length > 0) {
    if (data.length == 1) {
      // Single commission, return with new property values for name, id, etc
      if (setTeam) {
        const { id, userId, name, ...otherProps } = data[0];
        return {
          id: id + 1000,
          userId: userId + 1000,
          name: "Team",
          ...otherProps,
        };
      } else {
        return data;
      }
    } else {
      // Multiple commissions, calculate the averages and return
      return calculateAverages(data);
    }
  }
  return data; // Return data if no data
};

// This will take an array of commissions and
// will attempt to calculate the averages and return a single
// commission with those averages
// It will calculate averages for keys that are listed in a global array
// named combinable
export const calculateAverages = arr => {
  const initialObj = {};
  Object.assign(initialObj, arr[0]);
  for (let key in initialObj) {
    if (combinable.some(val => val === key)) {
      initialObj[key] = 0;
    }
  }
  initialObj["count"] = 0;
  const commissionTotalsObj = arr.reduce((commissionTotals, value) => {
    commissionTotals.count++;
    combinable.forEach(key => {
      commissionTotals[key] += value[key];
    });
    return commissionTotals;
  }, initialObj);
  // Get averages and return
  combinable.forEach(key => {
    const avg = Math.round(
      commissionTotalsObj[key] / commissionTotalsObj["count"]
    );
    commissionTotalsObj[key] = avg;
  });
  let { count, ...commissionAttributes } = commissionTotalsObj;
  return commissionAttributes;
};

// Set months and dates during initial load
export const setInitialMonthsYears = ({
  commissionsSet,
  setMonthSelectedStart,
  setMonthSelectedEnd,
  setYearOptions,
  setYearSelected,
}) => {
  // Get the latest commission and set the month as the selected month
  const latestDate = findLatestCommission(commissionsSet);
  setMonthSelectedStart(
    moment(latestDate)
      .startOf("month")
      .toDate()
  );
  setMonthSelectedEnd(
    moment(latestDate)
      .add(1, "month")
      .startOf("month")
      .toDate()
  );
  // Get all the years from the commissions and set the years option list
  const yearsOptionsList = createOptionsList(
    findCommissionYears(commissionsSet)
  );
  setYearOptions(yearsOptionsList);
  // Set the selected year to the most recent year (its sorted)
  if (
    yearsOptionsList &&
    Array.isArray(yearsOptionsList) &&
    yearsOptionsList.length > 0
  ) {
    setYearSelected(yearsOptionsList[0]["value"]);
  }
};

export const filterCommissionSubset = (
  commissionsAll: any,
  monthSelectedStart: any,
  monthSelectedEnd: any
) => {
  return commissionsAll.filter(commission => {
    const commissionDateMoment = moment(commission.month);
    return (
      (commissionDateMoment.isAfter(monthSelectedStart) ||
        commissionDateMoment.isSame(monthSelectedStart)) &&
      (commissionDateMoment.isBefore(monthSelectedEnd) ||
        commissionDateMoment.isSame(monthSelectedEnd))
    );
  });
};

interface ISetMonthProps {
  commissionsAll: any;
  monthSelectedStart: any;
  monthSelectedEnd: any;
  setLoadingMonths: any;
  setMonthData: any;
  setMonthTeamData?: any;
  user?: any;
  coreAverages?: any;
}

// Set month data
export const setFormattedMonthData = ({
  commissionsAll,
  monthSelectedStart,
  monthSelectedEnd,
  setLoadingMonths,
  setMonthData,
  setMonthTeamData,
  user,
}: ISetMonthProps) => {
  // Filter commissions
  const filteredCommissions = filterCommissionSubset(
    commissionsAll,
    monthSelectedStart,
    monthSelectedEnd
  );
  // Format the data
  const formattedDataMonths = user
    ? formatBarData(filteredCommissions, user)
    : formatBarData(filteredCommissions);
  // Create copy as to not alter original
  const formattedDataCopy = [...formattedDataMonths];
  // Combine the data for months (same user) and set to state
  const combinedDataMonthsUsers = combineCommissions(
    formattedDataCopy,
    "userId"
  );
  setMonthData(combinedDataMonthsUsers);
  // combine the data for team and set to state
  if (setMonthTeamData) {
    const combineDataMonthsTeam = combineCommissionsAll(formattedDataCopy);
    combineDataMonthsTeam["name"] = "Team";
    setMonthTeamData([combineDataMonthsTeam]);
  }
  setLoadingMonths(false);
};

// Set month data
export const setFormattedMonthDataRanked = ({
  commissionsAll,
  monthSelectedStart,
  monthSelectedEnd,
  setLoadingMonths,
  setMonthData,
  setMonthTeamData,
  user,
  coreAverages,
}: ISetMonthProps) => {
  // Filter commissions
  const filteredCommissions = filterCommissionSubset(
    commissionsAll,
    monthSelectedStart,
    monthSelectedEnd
  );
  // Exist if no records after filtering
  if (
    filteredCommissions &&
    Array.isArray(filteredCommissions) &&
    filteredCommissions.length === 0
  ) {
    return;
  }
  // Format the data
  const formattedDataMonths = user
    ? formatBarData(filteredCommissions, user)
    : formatBarData(filteredCommissions);
  // Create copy as to not alter original
  const formattedDataCopy = [...formattedDataMonths];
  // Combine the data for months (same user) and set to state
  const combinedDataMonthsUsers = combineCommissions(
    formattedDataCopy,
    "userId"
  );

  const sortedMonthData = combinedDataMonthsUsers.sort(
    (a, b) => b["Quota Average"] - a["Quota Average"]
  );

  setMonthData(sortedMonthData);
  // combine the data for team and set to state
  if (setMonthTeamData) {
    setMonthTeamData([
      {
        id: 999999,
        userId: 999999,
        name: "ALL Core Users",
        "Labor Profit": coreAverages["labor_average"],
        "New Agreements": coreAverages["agreements_average"],
        "Hardware / Software Profit": coreAverages["hardware_software_average"],
        "Quota Average": coreAverages["quota_average"],
      },
    ]);
  }
  setLoadingMonths(false);
};

interface ISetYearProps {
  commissionsAll: any;
  yearSelected: any;
  setLoadingYears: any;
  setYearData: any;
  user?: any;
  setYearTeamData?: any;
}

export const setFormattedYearsData = ({
  commissionsAll,
  yearSelected,
  setLoadingYears,
  setYearData,
  user,
  setYearTeamData,
}: ISetYearProps) => {
  const filteredCommissions = commissionsAll.filter(
    commission => moment(commission["month"]).format("YYYY") == yearSelected
  );
  // Format data for bar graph (year data)
  const formattedDataYears = user
    ? formatBarData(filteredCommissions, user)
    : formatBarData(filteredCommissions);
  // Create a copy for mutable concerns
  const formattedDataYearsCopy = [...formattedDataYears];
  // Combine the data for users
  const combinedDataYearsUsers = combineCommissions(
    formattedDataYearsCopy,
    "userId"
  );
  // Set year bar data into state
  setYearData(combinedDataYearsUsers);
  // Set team year data if callback provided
  if (setYearTeamData) {
    const combineDataYearTeam = combineCommissionsAll(formattedDataYearsCopy);
    combineDataYearTeam["name"] = "Team";
    setYearTeamData([combineDataYearTeam]);
  }
  setLoadingYears(false);
};

/**
 * Styled commponents for section containers
 */
export const ResponsiveContainer = styled.div`
  width: 570px;
  @media (max-width: 1487px) {
    width: 520px;
  }
  @media (max-width: 1393px) {
    width: 470px;
  }
  @media (max-width: 1292px) {
    width: 420px;
  }
  @media (max-width: 1192px) {
    width: 370px;
  }
  @media (max-width: 1092px) {
    width: 350px;
  }
  @media (max-width: 1069px) {
    width: 390px;
  }
  @media (max-width: 880px) {
    width: 350px;
  }
  @media (max-width: 802px) {
    width: 700px;
  }
  @media (max-width: 735px) {
    width: 630px;
  }
  @media (max-width: 665px) {
    width: 500px;
  }
`;

export const DashboardContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: space-around;
  align-items: top;
  flex-wrap: wrap;
  flex-direction: row;
`;

export const DashboardSectionContainer = styled(ResponsiveContainer)`
  display: flex;
  justify-content: flex-start;
  align-items: center;
  flex-wrap: wrap;
  flex-direction: column;
  border: 1px solid #ededed;
  min-height: 200px;
`;



// WEBPACK FOOTER //
// ./app/javascript/libs/commission_calculator_dashboard.ts